import { StatusIndicatorProps } from '@cloudscape-design/components';
import _, { chain } from 'lodash';
import moment from 'moment';
import {
  AUDIT_TYPE_LABELS,
  AuditStatus,
  AuditTypeOptions,
  ERROR_MESSAGES,
  LOCAL_STORAGE_KEYS,
  QuestionResultType,
} from 'src/contstant/commonLabels';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import { Persona } from 'src/contstant/Persona';
import { BusinessUnit } from 'src/types/orbit/BusinessUnit';
import { Role } from 'src/types/orbit/Roles';
import { PermissionResultMap } from 'src/types/orbit/User';
import { getFromLocalStorage } from './localStorageUtils';

export function convertList<T, U>(list: T[], converter: (item: T) => U): U[] {
  return list.map(converter);
}

export function convertItem<T, U>(item: T, converter: (item: T) => U): U {
  return converter(item);
}

// Enhanced error handling with version conflict detection
export const handleError = (error: any): string => {
  return (
    error?.response?.data?.message || // handles axios error response
    error?.data?.message || // handles custom error response
    error?.message || // handles JavaScript Error
    ERROR_MESSAGES.DEFAULT // fallback message
  );
};

// Extract full error data for version conflicts
export const getFullErrorData = (error: any) => {
  return error?.response?.data || error?.data || error;
};

// Detect version conflicts from error response
export const isVersionConflictError = (error: any): boolean => {
  const errorData = error?.response?.data || error?.data || error;
  return (
    errorData?.type === 'VersionConflictException' ||
    errorData?.message?.includes('version conflict') ||
    errorData?.message?.includes('Expected version')
  );
};

// Extract version conflict details
export const getVersionConflictDetails = (error: any) => {
  const errorData = error?.response?.data || error?.data || error;
  return {
    expectedVersion: errorData.expectedVersion || 'unknown',
    actualVersion: errorData.actualVersion || 'unknown',
    lastModifiedBy: errorData.lastModifiedBy || 'unknown',
    message: errorData.message || 'Version conflict detected',
  };
};

export const checkFeaturePermission = (
  persona: string,
  permissionKey: string,
  permissionResultMap?: PermissionResultMap,
): boolean => {
  if (persona === Persona.PLATFORM_ADMIN) {
    return true;
  }

  const defaultValue = false;

  // Check if permissionKey exists in permissionResultMap
  if (!permissionKey || !permissionResultMap) {
    return defaultValue;
  }

  // Return the permission value from the map, or undefined if not found
  return permissionKey in permissionResultMap
    ? permissionResultMap[permissionKey]
    : defaultValue;
};

export const formatDate = (
  dateInput?: string,
  inputFormat: string | null = null,
  outputFormat = 'YYYY-MM-DD',
): string => {
  try {
    if (!dateInput || dateInput === '') {
      return '';
    }

    let momentDate;

    // Handle different input types
    if (!isNaN(Number(dateInput)) && dateInput.toString().length > 8) {
      // Timestamp
      momentDate = moment.utc(parseInt(dateInput));
    } else if (inputFormat) {
      // Parse with specific input format
      momentDate = moment.utc(dateInput, inputFormat);
    } else {
      // Default date string parsing
      momentDate = moment.utc(dateInput);
    }

    if (!momentDate.isValid()) {
      console.error('Invalid date format:', dateInput);
      return '';
    }

    const displayDate = momentDate;
    return displayDate.format(outputFormat);
  } catch (error) {
    console.error('Invalid date format:', error);
    return '';
  }
};

export enum TimezoneType {
  UTC = 'UTC',
  SYSTEM = 'Local',
}

export const DateFormats = {
  SHORT_DATE: 'DD-MM-YYYY',
  LONG_DATE: 'DD MMM YYYY',
  FULL_DATE: 'DD MMMM YYYY',
  SHORT_DATETIME: 'DD-MM-YYYY HH:mm',
  LONG_DATETIME: 'DD MMM YYYY hh:mm a',
  TIME_ONLY: 'HH:mm:ss',
  ISO: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
};

export const formatDateTime = (
  epochTime?: number | string | Date,
  format?: string,
): string => {
  if (epochTime === undefined || epochTime === null) {
    return ERROR_MESSAGES.INVALID_DATE;
  }

  try {
    let timestamp: number;

    // Handle Date objects (e.g. from TypeScript client deserialization)
    if (epochTime instanceof Date) {
      timestamp = epochTime.getTime();
    } else if (typeof epochTime === 'string') {
      // Check if it's an ISO 8601 date string (supports variable decimal precision e.g. .123, .123456789)
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/.test(epochTime)) {
        timestamp = moment.utc(epochTime).valueOf();
      } else {
        // Check if it's in scientific notation
        const isScientificNotation = /\d+\.?\d*[eE][+-]?\d+/i.test(epochTime);

        if (isScientificNotation) {
          // Convert scientific notation and multiply by 1000 if it's in seconds
          const num = Number(epochTime);
          // Check if the number is in seconds (less than year 2100 in milliseconds)
          timestamp = num < 4102444800000 ? num * 1000 : num;
        } else {
          timestamp = Number(epochTime);
        }
      }
    } else {
      // If it's already a number, apply the same logic
      if (epochTime < 4102444800000) {
        timestamp = epochTime * 1000;
      } else {
        timestamp = epochTime;
      }
    }

    // If no format specified, use LONG_DATETIME format
    const dateFormat = format ?? DateFormats.LONG_DATETIME;

    // Check if format is date-only (no time components)
    const isDateOnly =
      !dateFormat.includes('H') &&
      !dateFormat.includes('h') &&
      !dateFormat.includes('m') &&
      !dateFormat.includes('s');

    // Create moment instance from normalized timestamp
    let dateTime = moment.utc(timestamp);

    // Only apply timezone conversion for formats that include time
    if (!isDateOnly) {
      const timezone = getFromLocalStorage<string>(
        LOCAL_STORAGE_KEYS.TIME_ZONE,
        TimezoneType.SYSTEM,
      );
      // If timezone is SYSTEM, convert to local time
      if (timezone === TimezoneType.SYSTEM) {
        dateTime = dateTime.local();
      }
    }

    return dateTime.format(dateFormat);
  } catch (error) {
    console.log('Error formatting date:', error);
    return ERROR_MESSAGES.INVALID_DATE;
  }
};

/**
 * Generic type filter function that can work with any two array types and their field names
 * @param sourceArray - Source array to filter
 * @param filterArray - Array containing filter criteria
 * @param sourceField - Field name from source array items to match against
 * @param filterField - Field name from filter array items to match against
 * @returns Filtered array of source type
 */
export const filterArrayByArray = <
  TSource extends Record<string, any>,
  TFilter extends Record<string, any> | string,
  K1 extends keyof TSource,
  K2 extends keyof TFilter,
>(
  sourceArray: TSource[],
  filterArray: TFilter[],
  sourceField: K1,
  filterField?: K2,
): TSource[] => {
  if (!sourceArray?.length || !filterArray?.length) {
    return [];
  }

  const filterValues = new Set(
    filterArray.map((item) => String(filterField ? item[filterField] : item)),
  );
  return sourceArray.filter((item) => filterValues.has(String(item[sourceField])));
};

export const findRolesById = (rolesList: Role[], roleId?: string): Role => {
  return rolesList.find((role: Role) => role.roleId === roleId) as Role;
};

export const findBusinessUnitById = (
  list?: BusinessUnit[],
  businessUnitId?: string,
): BusinessUnit | undefined => {
  if (!businessUnitId) {
    return undefined;
  }
  return list?.find(
    (data: BusinessUnit) => data.businessUnitId === businessUnitId,
  ) as BusinessUnit;
};

export const getAllPermissions = () => {
  // Transform permission values to include spaces between words
  const configPermissions = _.flatMap(permissionAccessConfig, (category) =>
    Object.values(category).map((permission) => {
      return permission;
    }),
  );

  return [...configPermissions];
};

/**
 * Converts a date string to ISO format with time set to midnight UTC
 * @param dateString - Date string in 'YYYY-MM-DD' format
 * @returns Formatted date string in 'YYYY-MM-DDTHH:mm:ssZ' format
 * @throws Error if invalid date string is provided
 */
export const formatToISOWithMidnight = (dateString: string): string => {
  if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    throw new Error('Invalid date format. Expected: YYYY-MM-DD');
  }

  const dateObj = moment.utc(dateString);

  if (!dateObj.isValid()) {
    throw new Error('Invalid date string provided');
  }

  return dateObj.toISOString().split('.')[0] + 'Z';
};

/**
 * Converts a date string to ISO format with time set to end of day (23:59:59) UTC
 * Used for audit expiration dates to ensure audits remain available through the entire calendar day
 * @param dateString - Date string in 'YYYY-MM-DD' format
 * @returns Formatted date string in 'YYYY-MM-DDTHH:mm:ssZ' format with time set to 23:59:59
 * @throws Error if invalid date string is provided
 */
export const formatToISOWithEndOfDay = (dateString: string): string => {
  if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    throw new Error('Invalid date format. Expected: YYYY-MM-DD');
  }

  const dateObj = moment.utc(dateString).endOf('day');

  if (!dateObj.isValid()) {
    throw new Error('Invalid date string provided');
  }

  return dateObj.toISOString().split('.')[0] + 'Z';
};

export const getAuditStatusIcon = (status: string) => {
  switch (status) {
    case AuditStatus.COMPLETED:
      return 'success';
    case AuditStatus.IN_PROGRESS:
      return 'in-progress';
    case AuditStatus.EXPIRED:
      return 'stopped';

    case AuditStatus.CANCELLED:
      return 'error';

    case AuditStatus.NOT_STARTED:
      return 'pending';
    default:
      return 'info';
  }
};

export const getAuditStatusColor = (status: string): StatusIndicatorProps.Color => {
  switch (status) {
    case AuditStatus.COMPLETED:
      return 'green';
    case AuditStatus.IN_PROGRESS:
      return 'blue';
    case AuditStatus.EXPIRED:
      return 'red';
    case AuditStatus.CANCELLED:
      return 'red';
    case AuditStatus.NOT_STARTED:
      return 'grey';
    default:
      return 'yellow';
  }
};

export const getAuditTypeOptions = ({
  isEditCalibrationAllow = false,
  isEditTestAudit = false,
  isEditProgressAllow = false,
  isEditSituationAllow = false,
  isEditMpaAllow = false,
} = {}) => {
  return Object.values(AuditTypeOptions).map((value) => {
    const label = AUDIT_TYPE_LABELS[value as keyof typeof AUDIT_TYPE_LABELS];
    const baseOption = {
      label,
      value,
      description: '',
    };

    const permissionCheck = {
      [AuditTypeOptions.CALIBRATION]: !isEditCalibrationAllow,
      [AuditTypeOptions.TEST]: !isEditTestAudit,
      [AuditTypeOptions.PROGRESS]: !isEditProgressAllow,
      [AuditTypeOptions.SITUATION]: !isEditSituationAllow,
      [AuditTypeOptions.MPA]: !isEditMpaAllow,
    };

    if (permissionCheck[value]) {
      return {
        ...baseOption,
        disabled: true,
        description: 'You do not have permission to select this.',
      };
    }

    return {
      ...baseOption,
      disabled: false,
    };
  });
};

export const getAuditQuestionResponseStatusIcon = (responseStatus?: string) => {
  switch (responseStatus) {
    case QuestionResultType.PASS:
      return 'success';
    case QuestionResultType.FAIL:
      return 'error';
    case QuestionResultType.NA:
      return 'info';
    default:
      return 'pending';
  }
};

export const calculatePercentage = (value: number, total: number): number => {
  if (total === 0) return 0;
  return chain(value).divide(total).multiply(100).round().value();
};

/**
 * Formats a numerical score for display with appropriate decimal precision
 * @param score - The numerical score value to format
 * @param maxDecimals - Maximum number of decimal places (default: 2)
 * @returns Formatted score string
 */
export const formatScore = (
  score: number | null | undefined,
  maxDecimals: number = 2,
): string => {
  // Handle edge cases
  if (score === null || score === undefined || isNaN(score) || !isFinite(score)) {
    return '-';
  }

  // Round to specified decimal places
  const multiplier = Math.pow(10, maxDecimals);
  const rounded = Math.round(score * multiplier) / multiplier;

  // Convert to string and remove trailing zeros
  const formatted = rounded.toFixed(maxDecimals);

  // Remove trailing zeros after decimal point
  return formatted.replace(/\.?0+$/, '');
};

/**
 * Parses a comma-separated string of aliases into a trimmed, lowercased array.
 * Filters out empty entries so inputs like ",,," produce an empty array.
 * @param input - Comma-separated aliases string (e.g. "user1, user2, user3")
 * @returns Array of cleaned alias strings
 */
export const parseAssignees = (input: string): string[] => {
  return input
    .split(',')
    .map((a) => a.trim().toLowerCase())
    .filter(Boolean);
};
